import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';

interface SystemInfoProps {
  systemData: any;
}

const SystemInfo: React.FC<SystemInfoProps> = ({ systemData }) => {
  if (!systemData) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Sistem Bilgileri
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sistem verileri yükleniyor...
        </Typography>
      </Paper>
    );
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}g ${hours}s`;
    if (hours > 0) return `${hours}s ${minutes}d`;
    return `${minutes}d`;
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Sistem Bilgileri
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* CPU Kullanımı */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <SpeedIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="body2">CPU Kullanımı</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={systemData.cpu?.percent || 0}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {systemData.cpu?.percent?.toFixed(1) || 0}%
          </Typography>
        </Box>

        {/* Bellek Kullanımı */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <MemoryIcon sx={{ mr: 1, color: 'secondary.main' }} />
            <Typography variant="body2">Bellek Kullanımı</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(systemData.memory?.used / systemData.memory?.total) * 100 || 0}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {formatBytes(systemData.memory?.used || 0)} / {formatBytes(systemData.memory?.total || 0)}
          </Typography>
        </Box>

        {/* Disk Kullanımı */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <StorageIcon sx={{ mr: 1, color: 'success.main' }} />
            <Typography variant="body2">Disk Kullanımı</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(systemData.disk?.used / systemData.disk?.total) * 100 || 0}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {formatBytes(systemData.disk?.used || 0)} / {formatBytes(systemData.disk?.total || 0)}
          </Typography>
        </Box>

        {/* Sistem Uptime */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <ComputerIcon sx={{ mr: 1, color: 'info.main' }} />
            <Typography variant="body2">Sistem Çalışma Süresi</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {formatUptime(systemData.uptime || 0)}
          </Typography>
        </Box>

        {/* Platform Bilgileri */}
        <Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Platform Bilgileri
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label={`Node.js ${systemData.node_version}`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`PM2 ${systemData.pm2_version}`}
                size="small"
                color="secondary"
                variant="outlined"
              />
              <Chip
                label={systemData.platform}
                size="small"
                color="default"
                variant="outlined"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default SystemInfo;
